/** @format */

import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import { checkVariables, extractFilterValues } from "utils/functions";
import { FadeLoader } from "react-spinners";
import Swal from "sweetalert2";
import { useBulkMarkAttendanceMutation } from "store/services/providerAPI";
import {
  extractErrorMessage,
  replaceUnderscoresWithSpaces,
} from "utils/strings";
import Empty from "images/emp.png";
import { ATTENDANCE_COLUMNS } from "./Columns";
import "./styles/filtering.css";
import AttendanceElements from "./AttendanceElements";
import { Button } from "react-bootstrap";
import { ReactComponent as UnMarkIcon } from "images/svg/unmark.svg";
import { tableEntries } from "utils/constant.data";

export const AttendanceTable = ({
  fellows,
  metadata,
  filter,
  filtering,
  program,
  cohort,
  schedule,
  refetch,

  setFilter,
  setSkip,
  setProgram,
  setCohorts,
  setSchedule,

  filterData,
  setShowFilter,
  setModalFilter,
}) => {
  const [entriesPerPage, setEntriesPerPage] = useState(tableEntries[0]);

  const [data, setData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkMarkAttendance, { isLoading }] = useBulkMarkAttendanceMutation();

  const handleRowSelection = useCallback(
    (id) => {
      if (selectedIds.includes(id)) {
        setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
      } else {
        setSelectedIds([...selectedIds, id]);
      }
    },
    [selectedIds]
  );

  const handleSelectAll = () => {
    if (!selectAll) {
      const allIds = fellows.map((_F) => _F?.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (filterData?.modalFilter) {
      // setSearch("");

      const values = extractFilterValues(filterData?.modalFilter);
      const filterString = `page=1&attendance_schedule=${
        schedule?.id
      }&program=${program}&page_size=${metadata?.page_size}${
        values?.attendanceMarked?.length > 0
          ? `&attendance_marked=${values?.attendanceMarked}`
          : ""
      }${
        values?.attendanceConfirmed?.length > 0
          ? `&attendance_confirmed=${values?.attendanceConfirmed}`
          : ""
      }`;

      // setFiltered(true);
      setFilter(filterString);
      setShowFilter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    setData([...fellows]);
    // setSkip(true);
  }, [fellows, setSkip]);

  useEffect(() => {
    if (filter) {
      setSkip(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const columns = useMemo(
    () =>
      ATTENDANCE_COLUMNS(
        selectedIds,
        handleRowSelection,
        cohort,
        schedule,
        program,
        refetch
      ),
    [selectedIds, handleRowSelection, cohort, schedule, program, refetch]
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const restoreFellows = () => {
    setFilter(
      `page=1&page_size=${metadata?.page_size}&attendance_schedule=${schedule?.id}&program=${program}`
    );
    setModalFilter(null);
    // setClear(true);
  };

  const handleSubmit = async () => {
    await bulkMarkAttendance({
      schedule_id: schedule?.id,
      status: "present",
      fellows: [...selectedIds],
      confirmation_page_link:
        "https://app.3mtt.training/fellow/confirm-attendance",
      program,
    })
      .unwrap()
      .then((data) => {
        Swal.fire({
          title: "Success",
          text: `${schedule?.label} Attendance has now been completed for ${
            selectedIds?.length
          } Fellow${selectedIds?.length > 1 ? "s" : ""}`,
          icon: "success",
          showCloseButton: true,
          customClass: {
            icon: "no-border",
          },
          confirmButtonText: "Ok",
        });
        setSelectedIds([]);
        refetch();
        // window.location.reload();
      })
      .catch((error) => {
        const formattedError = extractErrorMessage(error);
        Swal.fire({
          title:
            replaceUnderscoresWithSpaces(error?.data?.code) || "Error Occured",
          text: formattedError,
          iconHtml: ` <i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>`,
          showCloseButton: true,
          customClass: {
            icon: "no-border",
          },
          confirmButtonText: "Try Again",
        });
      });
  };

  const renderPaginationButtons = () => {
    const pageCount = Math.ceil(metadata?.count / metadata?.page_size);
    const totalPages = pageCount > 0 ? pageCount : 1;
    const lastPageIndex = (totalPages - 1) * metadata?.page_size;

    const buttons = [];

    // First page and previous buttons
    buttons.push(
      <button
        key="first"
        onClick={() => setFilter(`page=1&page_size=${metadata?.page_size}`)}
        className="pagination-button"
      >
        «
      </button>,
      <button
        key="prev"
        onClick={() =>
          setFilter(
            `page=${metadata?.page - 1}&page_size=${metadata?.page_size}`
          )
        }
        className={`pagination-button ${
          metadata?.page === 1 ? "disabled" : ""
        }`}
      >
        ‹
      </button>
    );

    // Page numbers
    for (let i = 1; i <= Math.min(8, totalPages); i++) {
      buttons.push(
        <button
          key={i}
          onClick={() =>
            setFilter(`page=${i}&page_size=${metadata?.page_size}`)
          }
          className={`pagination-button ${
            metadata?.page === i ? "active" : ""
          }`}
        >
          {i}
        </button>
      );
    }

    // Ellipsis and last page if needed
    if (totalPages > 8) {
      buttons.push(
        <span key="ellipsis" className="pagination-ellipsis">
          ...
        </span>,
        <button key="100" className="pagination-button">
          100
        </button>
      );
    }

    // Next and last page buttons
    buttons.push(
      <button
        key="next"
        onClick={() =>
          setFilter(
            `page=${metadata?.page + 1}&page_size=${metadata?.page_size}`
          )
        }
        className={`pagination-button ${
          metadata?.page === lastPageIndex + 1 ? "disabled" : ""
        }`}
      >
        ›
      </button>,
      <button
        key="last"
        onClick={() =>
          setFilter(
            `page=${lastPageIndex + 1}&page_size=${metadata?.page_size}`
          )
        }
        className="pagination-button"
      >
        »
      </button>
    );

    return buttons;
  };

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        {selectedIds?.length > 0 && (
          <>
            <p className="fw-bold mb-0 font-base">
              {selectedIds?.length} Fellow{selectedIds?.length > 1 ? "s" : ""}{" "}
              Selected
            </p>
            <div className="ml-2">
              <Button
                className="ms-2 me-2 py-3 btn-pigment-green-outline white-bg "
                style={{ width: "100% !important" }}
                onClick={() => {
                  Swal.fire({
                    title: "Confirm Action",
                    text: "Are you sure you want to proceed with this action?",
                    iconHtml: ` <i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>`,
                    showCloseButton: true,
                    customClass: {
                      icon: "no-border",
                    },

                    showCancelButton: true,
                    reverseButtons: true,
                    confirmButtonText: "Proceed",
                    cancelButtonText: "Cancel",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      handleSubmit();
                    }
                  });
                }}
              >
                {isLoading ? (
                  <span
                    role="status"
                    aria-hidden="true"
                    className="spinner-border spinner-border-sm ms-2"
                  ></span>
                ) : (
                  ` Mark Attendance`
                )}
                <UnMarkIcon />
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <AttendanceElements
          data={{ cohort: cohort?.label, schedule, program }}
          fellows={data}
          handleSelectAll={handleSelectAll}
          setProgram={setProgram}
          setCohorts={setCohorts}
          setSchedule={setSchedule}
        />

        {program && cohort && schedule ? (
          <div className={`card-body pt-0 ${isLoading ? "disabled " : ""}`}>
            <div className="table-responsive">
              <div className="dataTables_wrapper">
                {filtering ? (
                  <div className="d-flex justify-content-center align-items-center height-15 bg-white ">
                    <FadeLoader color="#36d7b7" />
                  </div>
                ) : (
                  <>
                    {data.length > 0 ? (
                      <>
                        <table
                          {...getTableProps()}
                          className="table dataTable display"
                        >
                          <thead>
                            {headerGroups.map((headerGroup) => (
                              <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column, index) => (
                                  <th
                                    {...column.getHeaderProps(
                                      column.getSortByToggleProps()
                                    )}
                                    style={{
                                      borderLeft:
                                        index ===
                                        headerGroup.headers?.length - 1
                                          ? "1px solid #e9ecef"
                                          : "none",
                                    }}
                                  >
                                    {column.render("Header")}
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody {...getTableBodyProps()}>
                            {rows.map((row) => {
                              prepareRow(row);
                              return (
                                <tr
                                  className="table--row"
                                  {...row.getRowProps()}
                                >
                                  {row.cells.map((cell, index) => {
                                    return (
                                      <td
                                        style={{
                                          borderLeft:
                                            index === row?.cells?.length - 1
                                              ? "1px solid #e9ecef"
                                              : "none",
                                        }}
                                        {...cell.getCellProps()}
                                      >
                                        {cell.render("Cell")}{" "}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        <div className="pagination-container">
                          <div className="entries-selector">
                            <span>Show</span>
                            <select
                              value={entriesPerPage}
                              onChange={(e) => {
                                setEntriesPerPage(Number(e.target.value));
                                setFilter(`page=1&page_size=${e.target.value}`);
                              }}
                            >
                              {tableEntries?.map((entry) => (
                                <option value={entry}>{entry}</option>
                              ))}
                            </select>
                            <span>Entries</span>
                          </div>

                          <div className="pagination-buttons">
                            {renderPaginationButtons()}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="card">
                        <div className="card-body text-center ai-icon  text-primary">
                          <figure>
                            <img src={Empty} alt="Empty Data" />
                          </figure>
                          <h4 className="my-2">
                            No fellows for the specified filter
                          </h4>
                          <button
                            onClick={() => restoreFellows()}
                            className="btn my-2  btn-pigment-green-outline btn-lg px-4 mx-2"
                          >
                            Clear Filter
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="card-body text-center ai-icon  text-primary">
            <figure>
              <img src={Empty} alt="Empty Data" />
            </figure>
            <h4 className="my-2">Add the required parameter</h4>
            <p>{checkVariables(program, cohort, schedule)}</p>
          </div>
        )}
      </div>
    </>
  );
};
export default AttendanceTable;
